import { hasRegion } from '@deighton/shared-ui/core';
import { Environment } from '../environments/types/environment.type';
import { isolateAuthStorage } from './auth-storage';
import { clearStoredRegionChoice, showRegionPicker } from './region-picker';
import {
  isSelectRegionPath,
  regionCookie,
  RegionResolution,
  resolveRegion,
  ShellRegion,
  suggestRegion,
  toRegion,
} from './region-resolver';
import { loadRuntimeConfig, ShellRuntimeConfig } from './runtime-config';

/**
 * Resolves the region before Angular boots: host rule, `/select-region`, cookie, or the picker.
 * Then wipes auth storage that belongs to another region.
 *
 * Development builds skip region resolution and keep the build-time API base, except on
 * {@link SELECT_REGION_PATH} so the pre-bootstrap picker can be exercised locally.
 * @param environment - The build environment.
 * @returns The region the shell talks to for this page load.
 */
export async function startRegion(environment: Environment): Promise<ShellRegion> {
  const pickerPreview: boolean = environment.development && isSelectRegionPath(window.location.pathname);

  if (environment.development && !pickerPreview) {
    return Object.freeze({
      key: 'local',
      label: 'Local',
      apiBaseUrl: environment.platformApiBaseUrl,
      source: 'build',
    });
  }

  const config: ShellRuntimeConfig = loadRuntimeConfig({ development: environment.development });
  const resolution: RegionResolution = resolveRegion(
    {
      hostname: window.location.hostname,
      pathname: window.location.pathname,
      search: window.location.search,
      cookie: document.cookie,
    },
    config.regions
  );

  let region: ShellRegion;
  if (resolution.kind === 'region') {
    region = resolution.region;
  } else {
    if (resolution.clearCookie) {
      clearStoredRegionChoice(document);
    }

    const url: URL = new URL(window.location.href);
    const forcedRegionKey: string = (url.searchParams.get('region') ?? '').trim();
    if (isSelectRegionPath(url.pathname) && forcedRegionKey && hasRegion(config.regions, forcedRegionKey)) {
      document.cookie = regionCookie(forcedRegionKey);
      region = toRegion(
        config.regions,
        forcedRegionKey,
        config.regions[forcedRegionKey].apiBaseUrl,
        'picker'
      );
      url.searchParams.delete('region');
      window.history.replaceState(window.history.state, '', `/${url.search}${url.hash}`);
    } else {
      const suggested: string | null = suggestRegion(
        Intl.DateTimeFormat().resolvedOptions().timeZone,
        config.regions
      );
      const key: string = await showRegionPicker(config.regions, suggested);
      document.cookie = regionCookie(key);
      region = toRegion(config.regions, key, config.regions[key].apiBaseUrl, 'picker');
      // `/select-region` → `/` so the next load follows the cookie; `login_hint` was stripped in main.ts.
      if (isSelectRegionPath(url.pathname)) {
        window.history.replaceState(window.history.state, '', `/${url.search}${url.hash}`);
      }
    }
  }

  isolateAuthStorage(region.key, window.localStorage, window.sessionStorage);
  return region;
}
